import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  styled,
  Typography
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import PrimaryButton from '../buttons/primaryButton';

import AddGameDialog from '../../games/addGameDialog';

import { useAuth } from '../../../contexts/authContext';
import { Roles, hasWorkspaceAccess } from '../../../helpers/access';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import { Edit } from '@mui/icons-material';
import SplitSectionDialog from '../../section/splitSectionDialog';
import SetupSectionDraw from '../../section/setupSectionDraw';
import GenerateSectionGames from '../../section/generateSectionGames';

import MenuIcon from '@mui/icons-material/Menu';
import AddEditSectionDialog from '../../events/addEditSectionDialog';
import { deleteSection, finaliseSection } from '../../../features/event/eventSlice';
import { mockRoundResults, resetRoundGames } from '../../../features/game/gameSlice';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditIcon from '@mui/icons-material/Edit';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DeleteIcon from '@mui/icons-material/Delete';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import GridViewIcon from '@mui/icons-material/GridView';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { Link, useNavigate } from 'react-router-dom';
import { GameStatus } from '../../../constants/gameConstants';
import { liveGameDocs } from '../../../helpers/games';

const EventSectionCard = ({ event, section, onViewSection, onUpdateSection }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authenticated, currentUserProfile, currentWorkspace } = useAuth();
  const [loadingSection, setLoadingSection] = useState(false);
  const [games, setGames] = useState([]);

  const deletingSection = useSelector((state) => state.event.deletingSection);
  const finalisingSectionIds = useSelector((state) => state.event.finalisingSectionIds);
  const isFinalising = finalisingSectionIds.includes(section.id);

  const [openSplit, setOpenSplit] = useState(false);
  const [openAddEditSection, setOpenAddEditSection] = useState(false);
  const [openFinaliseSectionConfirm, setOpenFinaliseSectionConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openGenerateGames, setOpenGenerateGames] = useState(false);
  const [openDraws, setOpenDraws] = useState(false);
  const [openMockRoundDialog, setOpenMockRoundDialog] = useState(false);
  const [selectedRoundToMock, setSelectedRoundToMock] = useState(event?.currentRound || 1);
  const [mockingRound, setMockingRound] = useState(false);
  const [mockRoundQueued, setMockRoundQueued] = useState(false);
  const [openResetRoundDialog, setOpenResetRoundDialog] = useState(false);
  const [selectedRoundToReset, setSelectedRoundToReset] = useState(event?.currentRound || 1);
  const [resettingRound, setResettingRound] = useState(false);
  const [resetRoundQueued, setResetRoundQueued] = useState(false);

  const [canFinalise, setCanFinalise] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Get games based on event and section.
  useEffect(() => {
    setLoadingSection(true);

    // Get games where event = event.id and section = section.id
    const gamesRef = collection(store, 'games');
    const gamesQuery = query(
      gamesRef,
      where('event', '==', event.id),
      where('section', '==', section.id)
    );

    const unsubscribe = onSnapshot(gamesQuery, (querySnapshot) => {
      // Filtered at the source: canFinalise below is "every game in this
      // section is COMPLETE", and a deleted game is never going to be, so
      // leaving one in would disable Finalise for good.
      const gamesList = liveGameDocs(querySnapshot).map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setGames(gamesList);
      setLoadingSection(false);
    });

    return () => unsubscribe();
  }, [event.id, section.id, dispatch]);

  useEffect(() => {
    if (!games || !section) return;
    const gamesInSection = games.filter((g) => g.section === section.id);
    const allGamesCompleted = gamesInSection.every((g) => g.status === GameStatus.COMPLETE);
    setCanFinalise(allGamesCompleted); //&& !section.finalised
  }, [games, section]);

  const getAllPlayersInSection = () => {
    const teams = event.teams;
    const teamPlayerNames = [];

    if (games && games.length > 0) {
      for (let i = 0; i < games.length; i++) {
        const game = games[i];

        const teamA = teams.find((t) => t.id === game.teamA);
        const teamB = teams.find((t) => t.id === game.teamB);

        if (!teamA?.name || !teamB?.name) {
          continue;
        }

        // console.log('teamA', teamA);
        // console.log('teamB', teamB);

        if (teamPlayerNames.indexOf(teamA?.name) === -1) teamPlayerNames.push(teamA?.name);
        if (teamPlayerNames.indexOf(teamB?.name) === -1) teamPlayerNames.push(teamB?.name);
      }
    }

    return teamPlayerNames;
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSplitOpen = () => {
    setOpenSplit(true);
    setAnchorEl(null);
  };

  const handleSplitClose = () => {
    setOpenSplit(false);
  };

  const handleSectionAddEditClose = () => {
    setOpenAddEditSection(false);
  };

  const handleSectionAddEditOpen = () => {
    setOpenAddEditSection(true);
    setAnchorEl(null);
  };

  const openFinaliseSection = () => {
    setOpenFinaliseSectionConfirm(true);
    setAnchorEl(null);
  };

  const closeFinaliseSection = () => {
    setOpenFinaliseSectionConfirm(false);
  };

  const handleFinaliseSection = () => {
    // Finalise the section
    dispatch(finaliseSection({ eventId: event.id, sectionId: section.id }));
    closeFinaliseSection();
  };

  const handleOpenDelete = () => {
    setOpenDeleteConfirm(true);
    setAnchorEl(null);
  };

  const handleDeleteSection = async () => {
    await dispatch(deleteSection({ eventId: event.id, sectionId: section.id }));
    setOpenDeleteConfirm(false);
  };

  const handleOpenGeneratingGames = () => {
    setOpenGenerateGames(true);
    setAnchorEl(null);
  };

  const handleGeneratingGamesClose = () => {
    setOpenGenerateGames(false);
  };

  const handleOpenDraws = () => {
    setOpenDraws(true);
    setAnchorEl(null);
  };

  const handleDrawsClose = () => {
    setOpenDraws(false);
  };

  const handleOpenMockRoundDialog = () => {
    setSelectedRoundToMock(event?.currentRound || 1);
    setMockRoundQueued(false);
    setOpenMockRoundDialog(true);
    setAnchorEl(null);
  };

  const handleCloseMockRoundDialog = () => {
    setMockRoundQueued(false);
    setOpenMockRoundDialog(false);
  };

  const handleOpenResetRoundDialog = () => {
    setSelectedRoundToReset(event?.currentRound || 1);
    setResetRoundQueued(false);
    setOpenResetRoundDialog(true);
    setAnchorEl(null);
  };

  const handleCloseResetRoundDialog = () => {
    setResetRoundQueued(false);
    setOpenResetRoundDialog(false);
  };

  const handleConfirmMockRound = async () => {
    setMockingRound(true);
    try {
      await dispatch(
        mockRoundResults({
          eventId: event.id,
          sectionId: section.id,
          round: Number(selectedRoundToMock)
        })
      ).unwrap();
      setMockRoundQueued(true);
    } catch (error) {
      console.error('Failed to generate mock round results:', error);
    } finally {
      setMockingRound(false);
    }
  };

  const handleConfirmResetRound = async () => {
    setResettingRound(true);
    try {
      await dispatch(
        resetRoundGames({
          eventId: event.id,
          sectionId: section.id,
          round: Number(selectedRoundToReset)
        })
      ).unwrap();
      setResetRoundQueued(true);
    } catch (error) {
      console.error('Failed to reset round games:', error);
    } finally {
      setResettingRound(false);
    }
  };

  const renderMenu = () => (
    <>
      <IconButton size="small" color="text" onClick={handleMenuClick}>
        <MenuIcon />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        sx={{
          '.MuiMenu-paper': {
            backgroundColor: 'background.main'
          }
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            sx: {
              py: 0
            }
          }
        }}>
        <ListSubheader
          dense
          sx={{
            backgroundColor: 'background.main'
          }}>
          Games
        </ListSubheader>

        <MenuItem
          dense
          onClick={() => navigate('/events/' + event.id + '/section/' + section.id)}
          disabled={games.length === 0}>
          <ListItemIcon>
            <GridViewIcon fontSize="small" color="text" />
          </ListItemIcon>
          <ListItemText>View Section Games</ListItemText>
        </MenuItem>
        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <MenuItem
            dense
            onClick={handleOpenGeneratingGames}
            // disabled={
            //   section?.finalised ||
            //   games.length > 0 ||
            //   event.currentRound !== event.gameFormat.rounds
            // }
          >
            <ListItemIcon>
              <AutorenewIcon fontSize="small" color="text" />
            </ListItemIcon>
            <ListItemText>Generate Section Games</ListItemText>
          </MenuItem>
        )}
        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER) && (
          <MenuItem dense onClick={handleOpenMockRoundDialog} disabled={games.length === 0}>
            <ListItemIcon>
              <ShuffleIcon fontSize="small" color="text" />
            </ListItemIcon>
            <ListItemText>Generate Mock Results</ListItemText>
          </MenuItem>
        )}
        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <MenuItem dense onClick={handleOpenResetRoundDialog} disabled={games.length === 0}>
            <ListItemIcon>
              <RestartAltIcon fontSize="small" color="text" />
            </ListItemIcon>
            <ListItemText>Reset Round Games</ListItemText>
          </MenuItem>
        )}

        <ListSubheader
          dense
          sx={{
            backgroundColor: 'background.main'
          }}>
          Section
        </ListSubheader>
        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <MenuItem
            dense
            onClick={handleOpenDraws}
            sx={{
              color: 'blue.main'
            }}
            disabled={section?.finalised || games.length > 0}>
            <ListItemIcon>
              <FormatListNumberedIcon fontSize="small" color="blue" />
            </ListItemIcon>
            <ListItemText>Setup Draws</ListItemText>
          </MenuItem>
        )}

        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <MenuItem
            dense
            onClick={openFinaliseSection}
            sx={{
              color: 'success.main'
            }}
            disabled={
              !(canFinalise && !section?.winningTeam && section?.id) // && section.winnerPlaysSection
            }>
            <ListItemIcon>
              <DoneAllIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Finalise Section</ListItemText>
          </MenuItem>
        )}

        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && !section.splitTo && (
          <MenuItem onClick={handleSplitOpen} disabled={section?.finalised} dense>
            <ListItemIcon>
              <AltRouteIcon fontSize="small" color="text" />
            </ListItemIcon>
            <ListItemText>Split Section</ListItemText>
          </MenuItem>
        )}

        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <MenuItem onClick={() => handleSectionAddEditOpen()} dense>
            <ListItemIcon>
              <EditIcon fontSize="small" color="text" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}

        {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <MenuItem
            sx={{
              // backgroundColor: 'error.main',
              color: 'error.main'
            }}
            dense
            onClick={() => handleOpenDelete(true)}
            disabled={deletingSection}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );

  return (
    <Fragment>
      <Card sx={{ minWidth: 350, mb: 1, mr: 2 }}>
        <CardHeader
          title={
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}>
              <MuiLink
                component={Link}
                to={'/events/' + event.id + '/section/' + section.id}
                color="primary"
                underline="hover">
                {section.name}
              </MuiLink>

              <Box>{renderMenu()}</Box>
            </Box>
          }
          subheader={
            !loadingSection &&
            games && (
              <>
                <Typography variant={'caption'}>Total Games: {games ? games.length : 0}</Typography>
              </>
            )
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {!loadingSection && section.initialDraw ? (
            <Box display={'flex'} flexDirection={'column'}>
              <Typography variant={'h6'}>Players/Teams:</Typography>
              {section.initialDraw.map((team, index) => (
                <Typography key={index} variant="body2">
                  {team.position}. {team.name}
                </Typography>
              ))}
            </Box>
          ) : !loadingSection && games ? (
            <Fragment>
              <Typography variant={'h6'}>Players:</Typography>
              {getAllPlayersInSection().map((player, index) => (
                <Typography key={index} variant="body2">
                  {index + 1}. {player}
                </Typography>
              ))}
            </Fragment>
          ) : (
            loadingSection && <Typography variant={'caption'}>Loading {section.name}...</Typography>
          )}
        </CardContent>
        <CardActions
          sx={{ justifyContent: 'space-between', alignItems: 'center', justifySelf: 'flex-end' }}>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
            {isFinalising ? (
              <>
                <CircularProgress size={16} sx={{ color: 'yellow.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Finalising section…
                </Typography>
              </>
            ) : (
              <>
                {section?.finalised && (
                  <Typography variant="caption" color="success">
                    Section won by {section.winner?.teamName}
                  </Typography>
                )}
                {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && !section?.finalised && (
                  <AddGameDialog event={event} section={section} />
                )}
              </>
            )}
          </Box>
        </CardActions>
      </Card>

      {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
        <SplitSectionDialog
          event={event}
          section={section}
          open={openSplit}
          onClose={handleSplitClose}
        />
      )}

      <AddEditSectionDialog
        open={openAddEditSection}
        onClose={() => handleSectionAddEditClose()}
        event={event}
        section={section}
      />

      <Dialog
        open={openMockRoundDialog}
        onClose={handleCloseMockRoundDialog}
        aria-labelledby="mock-round-dialog-title"
        aria-describedby="mock-round-dialog-description">
        <DialogTitle id="mock-round-dialog-title">Generate Mock Results</DialogTitle>
        <DialogContent>
          <DialogContentText id="mock-round-dialog-description" sx={{ mb: 2 }}>
            {mockRoundQueued
              ? 'Mock result generation has been queued. It is running in the backend and may take a while for games to update.'
              : 'Select the round to generate mock results for all games in this section.'}
          </DialogContentText>
          {!mockRoundQueued && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Round
              </Typography>
              <select
                value={selectedRoundToMock}
                onChange={(e) => setSelectedRoundToMock(Number(e.target.value))}
                style={{ width: '100%', padding: 8, borderRadius: 6 }}>
                {Array.from({ length: Number(event?.gameFormat?.rounds || 1) }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    Round {index + 1}
                  </option>
                ))}
              </select>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMockRoundDialog} color="primary" disabled={mockingRound}>
            {mockRoundQueued ? 'Close' : 'Cancel'}
          </Button>
          {!mockRoundQueued && (
            <Button
              onClick={handleConfirmMockRound}
              color="info"
              variant="contained"
              autoFocus
              disabled={mockingRound}>
              {mockingRound ? 'Starting...' : 'Generate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResetRoundDialog}
        onClose={handleCloseResetRoundDialog}
        aria-labelledby="reset-round-dialog-title"
        aria-describedby="reset-round-dialog-description">
        <DialogTitle id="reset-round-dialog-title">Reset Round Games</DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-round-dialog-description" sx={{ mb: 2 }}>
            {resetRoundQueued
              ? 'Round reset has been queued. It is running in the backend and games will update once completed.'
              : 'Select the round to reset all games in this section back to not started.'}
          </DialogContentText>
          {!resetRoundQueued && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Round
              </Typography>
              <select
                value={selectedRoundToReset}
                onChange={(e) => setSelectedRoundToReset(Number(e.target.value))}
                style={{ width: '100%', padding: 8, borderRadius: 6 }}>
                {Array.from({ length: Number(event?.gameFormat?.rounds || 1) }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    Round {index + 1}
                  </option>
                ))}
              </select>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetRoundDialog} color="primary" disabled={resettingRound}>
            {resetRoundQueued ? 'Close' : 'Cancel'}
          </Button>
          {!resetRoundQueued && (
            <Button
              onClick={handleConfirmResetRound}
              color="warning"
              variant="contained"
              autoFocus
              disabled={resettingRound}>
              {resettingRound ? 'Starting...' : 'Reset Round'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <SetupSectionDraw
        event={event}
        section={section}
        open={openDraws}
        onClose={handleDrawsClose}
      />

      {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
        <>
          <GenerateSectionGames
            event={event}
            section={section}
            sectionGames={games}
            open={openGenerateGames}
            onClose={handleGeneratingGamesClose}
          />
        </>
      )}

      <Dialog open={openFinaliseSectionConfirm} onClose={closeFinaliseSection}>
        <DialogTitle id="alert-dialog-title">Finalise Section</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to finalise this section? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFinaliseSection}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleFinaliseSection}>
            Finalise
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Delete Section</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this section? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)} disabled={deletingSection}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSection}
            disabled={deletingSection}>
            {deletingSection ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default EventSectionCard;
